import PropTypes from "prop-types";
import styled from "styled-components";

import { COLOR_VALUES } from "../../../config/colors";
import Icon, { ICON_NAMES } from "../../utils/Icon";
import Text from "shared/components/data-display/text/Text";
import BaseButton from "./BaseButton";
import Tooltip from "shared/components/utils/tooltip/Tooltip";

const getPadding = ({ $reducePadding, $isSmall, $isMedium }) => {
    if ($isSmall) return "6px 14px";
    if ($isMedium) return "10px 36px";
    if ($reducePadding) return "14px 16px";
    return "14px 48px";
};

const getMinHeight = ({ $isSmall, $isMedium }) => {
    if ($isSmall) return "32px";
    if ($isMedium) return "36px";
    return "48px";
};

const StyledPrimaryButton = styled(BaseButton)`
    display: inline-flex;
    gap: 4px;
    align-items: center;
    justify-content: center;
    background: ${COLOR_VALUES.Black};
    border-radius: 4px;
    color: ${COLOR_VALUES.White} !important; /* !important to override css reboot */
    text-decoration: none !important; /* !important to override css reboot */
    padding: ${getPadding};
    min-height: ${getMinHeight};

    &:not(:disabled) {
        &:hover {
            background: ${COLOR_VALUES.Gray_1};
        }

        &:active {
            box-shadow: 0 0 0px 2px ${COLOR_VALUES.Black} inset;
        }
    }

    &:disabled {
        background: ${COLOR_VALUES.Gray_3};
    }
`;

const PrimaryButton = ({
    label,
    leadingIcon,
    trailingIcon,
    isSmall,
    isMedium,
    ...rest
}) => {
    const ariaLabel = rest["aria-label"];

    if (!label && !ariaLabel) {
        console.error("Icon-only button missing aria-label");
    }

    let textVariant = "Headline_5";
    if (isSmall) textVariant = "Descriptive_1_2";
    else if (isMedium) textVariant = "Body_2_2";

    return (
        <Tooltip text={ariaLabel} enabled={!label}>
            <StyledPrimaryButton
                $reducePadding={!label}
                $isSmall={isSmall}
                $isMedium={isMedium}
                {...rest}
            >
                {leadingIcon && <Icon name={leadingIcon} />}
                {label && <Text variant={textVariant}>{label}</Text>}
                {trailingIcon && <Icon name={trailingIcon} />}
            </StyledPrimaryButton>
        </Tooltip>
    );
};

PrimaryButton.propTypes = {
    label: PropTypes.string,
    leadingIcon: PropTypes.oneOf(ICON_NAMES),
    trailingIcon: PropTypes.oneOf(ICON_NAMES),
    "aria-label": PropTypes.string,
    disabled: PropTypes.bool,
    isSmall: PropTypes.bool,
    isMedium: PropTypes.bool,
};

export default PrimaryButton;
